import { useMemo, useState } from "react";
import { CopyEmailButton } from "@/components/common/CopyableEmail";
import QRCode from "qrcode.react";
import { Check, Copy } from "lucide-react";
import { trackCallClick } from "@/lib/ga4";
import type { HelpHubDTO } from "@/types/help-hub/help-hub";
import {
  buildGenieTalkUrl,
  formatGenieShareLabel,
} from "@/utils/agent/genieShareLinks";
import { hubBrandTheme, inkOn, tint } from "../brandInk";
import { HubSection } from "./HubSection";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface HubAccessSectionProps {
  hub: HelpHubDTO;
  displayName: string;
  /** Opens the Genie on its start screen. */
  onTalk: () => void;
}

/**
 * "Every way in" — the access methods, shown together and unmissably.
 *
 * The hero's channel strip is a wayfinder; this band is the reference card. It
 * puts chat, phone, email, the shareable talk link and a scannable QR in one
 * place, so a visitor who scrolled past the hero — or someone sent here to
 * find "the number" — never has to hunt. Methods a Genie doesn't have are
 * omitted, and the two that every Genie has (chat and the link) mean the band
 * is never empty, even on a page with nothing published yet.
 */
export const HubAccessSection = ({ hub, displayName, onTalk }: HubAccessSectionProps) => {
  const { branding } = hub;
  const theme = hubBrandTheme(branding);
  const [copied, setCopied] = useState(false);

  // The same talk link the share bars hand out, minus the receive intent — a
  // visitor scanning a help page wants a conversation, not a claim flow.
  const talkUrl = useMemo(() => buildGenieTalkUrl(hub.urlName), [hub.urlName]);
  const talkLabel = formatGenieShareLabel(hub.urlName);

  const copyTalkUrl = async () => {
    if (!talkUrl) return;
    try {
      await navigator.clipboard.writeText(talkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (permissions, http) — the visible URL
      // remains selectable, so failing silently beats an error nobody can act on.
    }
  };

  const methodRow = (props: {
    key: string;
    genieIcon: GenieIconName;
    label: string;
    detail: string;
    href?: string;
    onClick?: () => void;
    trailing?: React.ReactNode;
  }) => {
    const body = (
      <>
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
          style={{ backgroundColor: tint(theme.primary, 0.08) }}
        >
          <GenieIcon
            name={props.genieIcon}
            size={20}
            color={theme.primary}
            secondaryColor={theme.secondary}
            gradientEnabled={theme.gradientOn}
            label=""
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14.5px] font-semibold leading-snug text-genie-text-primary">
            {props.label}
          </span>
          <span className="block truncate text-[12.5px] text-genie-text-muted">
            {props.detail}
          </span>
        </span>
        {props.trailing}
      </>
    );
    const className =
      "flex min-h-[44px] w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-genie-background sm:px-5";

    if (props.href) {
      return (
        <div key={props.key} className="flex min-w-0 items-center pr-4">
          <a
            href={props.href}
            className={className}
            onClick={props.href.startsWith("tel:") ? () => trackCallClick("hub_access_section") : undefined}
          >
            {body}
          </a>
          {props.key === "email" && <CopyEmailButton email={props.detail} />}
        </div>
      );
    }
    if (props.onClick) {
      return (
        <button key={props.key} type="button" onClick={props.onClick} className={className}>
          {body}
        </button>
      );
    }
    return (
      <div key={props.key} className={className}>
        {body}
      </div>
    );
  };

  const rows = [
    methodRow({
      key: "chat",
      genieIcon: "chat",
      label: "Start chatting",
      detail: "Ask by voice or text — answers in seconds, day or night",
      onClick: onTalk,
    }),
    ...(hub.phoneNumber
      ? [
          methodRow({
            key: "call",
            genieIcon: "phone",
            label: "Call us",
            detail: hub.phoneNumber,
            href: `tel:${hub.phoneNumber}`,
          }),
        ]
      : []),
    ...hub.extraPhones.map((phone) =>
      methodRow({
        key: `call-${phone.id}`,
        genieIcon: "phone",
        label: phone.label || "Call us",
        detail: phone.number,
        href: `tel:${phone.number}`,
      }),
    ),
    ...(hub.emailAddress
      ? [
          methodRow({
            key: "email",
            genieIcon: "mail",
            label: "Send an email",
            detail: hub.emailAddress,
            href: `mailto:${hub.emailAddress}`,
          }),
        ]
      : []),
    ...(talkUrl && talkLabel
      ? [
          methodRow({
            key: "link",
            genieIcon: "share",
            label: "Talk on a link",
            detail: talkLabel,
            onClick: copyTalkUrl,
            trailing: (
              <span
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold"
                style={{ backgroundColor: tint(theme.primary, 0.1), color: theme.primary }}
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" strokeWidth={2.25} /> Copy
                  </>
                )}
              </span>
            ),
          }),
        ]
      : []),
  ];

  return (
    <HubSection
      id="ways-in"
      title="Every way to reach us"
      subtitle="Talk, call, email or share the link — whichever suits the moment."
      genieIcon="share"
      branding={branding}
    >
      <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
        <div className="flex flex-col overflow-hidden rounded-2xl border bg-white"
          style={{ borderColor: theme.withAlpha(theme.primary, "26") }}
        >
          <div className="divide-y divide-genie-border">{rows}</div>
          <div className="mt-auto border-t border-genie-border p-4 sm:px-5">
            <button
              type="button"
              onClick={onTalk}
              className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl px-6 py-3 text-[14px] font-semibold transition-opacity hover:opacity-90 sm:w-auto"
              style={{ background: theme.gradient, color: inkOn(theme.primary) }}
            >
              Ask {displayName} now
            </button>
          </div>
        </div>

        {/* Desktop: hand the conversation to a phone. Hidden on phones, where
            every method above is already one tap. */}
        {talkUrl && (
          <div className="hidden flex-col items-center justify-center gap-3 rounded-2xl p-[2px] sm:flex"
            style={{ background: theme.gradient }}
          >
            <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-[14px] bg-white p-6">
              <div
                className="rounded-2xl border p-3"
                style={{ borderColor: theme.withAlpha(theme.primary, "40") }}
              >
                <QRCode value={talkUrl} size={132} level="M" fgColor="#111827" bgColor="#FFFFFF" />
              </div>
              <p className="text-center text-[12.5px] text-genie-text-muted">
                Scan to talk to {displayName} on your phone
              </p>
            </div>
          </div>
        )}
      </div>
    </HubSection>
  );
};

export default HubAccessSection;
